/**
 * Reusable entity validation: types and helpers used across all entity types.
 * Entity-type-specific rules live in per-type files (e.g. groupValidation.ts).
 */

/** Single validation message (warning or info). */
export interface ValidationWarning {
    message: string;
    severity?: 'warning' | 'info';
    /** When set, this warning applies to these specific attribute(s). Omit for entity-level issues. */
    attributeDisplayNames?: string[];
}

/** Result of a strict validation (e.g. save-blocking). */
export interface ValidationResult {
    isValid: boolean;
    error: string | null;
}

/**
 * Read an attribute from entity or form data.
 * Tries both plain key and $.key so it works for view (entity) and edit (form) data.
 */
export function getEntityAttr(data: Record<string, any>, attrName: string): any {
    if (!data || typeof data !== 'object') return undefined;
    const v = data[attrName] ?? data[`$.${attrName}`] ?? data[attrName.charAt(0).toLowerCase() + attrName.slice(1)];
    if (v === undefined || v === null) return undefined;
    return v;
}

/**
 * String value for an attribute (trimmed). Handles reference objects (Id/DisplayName).
 */
export function getEntityAttrString(data: Record<string, any>, attrName: string): string {
    const v = getEntityAttr(data, attrName);
    if (v == null) return '';
    if (typeof v === 'string') return v.trim();
    if (typeof v === 'object' && v !== null) {
        const s = (v as any).Id ?? (v as any).id ?? (v as any).DisplayName ?? (v as any).displayName;
        return s != null ? String(s).trim() : '';
    }
    return String(v).trim();
}

/**
 * Resolve a form value by trying multiple keys (form may use "DisplayName" or "$.DisplayName" per tier3).
 */
export function getFormValue(formData: Record<string, any>, keys: string[]): any {
    if (!formData || typeof formData !== 'object') return undefined;
    for (const k of keys) {
        const v = formData[k];
        if (v !== undefined && v !== null && v !== '') return v;
    }
    return undefined;
}

/**
 * Resolve display name value from form (form may key as "DisplayName" or "$.DisplayName" per tier3).
 */
export function getDisplayNameFromForm(formData: Record<string, any>): string {
    const v = getFormValue(formData, ['DisplayName', '$.DisplayName', 'displayName', '$.displayName']);
    return (v != null && typeof v === 'string') ? String(v).trim() : '';
}
